import { Spinner2, Text2 } from "@el8/vital";
import { EhrConnector } from "@elation/api-client/src/stitch-web/models/ehrConnector";
import { FloatingStitchButton } from "@src/components/general/FloatingStitchButton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@src/components/ui/dialog";
import {
  isIframeReadyMessage,
  isIframeRequestAuthTokenMessage,
  parentInitMessage,
  parentSendAuthTokenMessage,
  type PatientFromIframe,
  type TranscriptionStartedPayload,
  type TranscriptionStoppedPayload,
} from "@src/features/iframe";
import { ReactQueryClientProvider } from "@src/providers/reactQuery";
import { queryClient } from "@src/queryClient";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type { PatientFromIframe as StitchPatientSearch };

export interface StitchIframeProps {
  /**
   * The environment to connect to.
   */
  environment: "production" | "stage" | "sandbox" | "dev" | "local";
  /**
   * The width of the dialog.
   * @default "32.25rem"
   */
  dialogWidth?: string;
  /**
   * The email of the current user.
   */
  email: string;
  /**
   * The patient search request.
   */
  patient: PatientFromIframe;
  /**
   * The authentication token.
   *
   * @example
   * ```tsx
   * const authToken = "1234567890";
   * ```
   * @default () => ""
   */
  authToken?: string;
  /**
   * Called when the iframe asks the parent for a fresh auth token.
   *
   * The iframe re-asks for a token whenever its bearer has gone stale
   * (e.g. session refetch returned 401 mid-conversation). If this is
   * provided, the parent actively mints/refreshes the token rather than
   * echoing whatever {@link authToken} happens to be set right now —
   * which is what unblocks recovery from mid-session expirations
   * without bouncing the user to /login.
   *
   * Resolve with `null` (or throw) if the parent cannot produce a token;
   * the iframe will then surface a real error rather than spinning.
   */
  onRefreshAuthToken?: () => Promise<string | null>;
  /**
   * When set, forwards a transcription-started event to the iframe.
   * Elation sets this when the user starts transcription.
   */
  transcription?: TranscriptionStartedPayload | null;
  /**
   * When set, forwards a transcription-stopped event to the iframe.
   * Elation sets this when the user stops transcription.
   */
  transcriptionStopped?: TranscriptionStoppedPayload | null;
  /**
   * EHR connector for {@link ehrPatientId} (defaults to `elation` when `ehrPatientId` is set).
   */
  ehr: EhrConnector;
  render?: React.ComponentType<
    React.PropsWithChildren<{
      iframeReady: boolean;
      iframe: HTMLIFrameElement | null;
      isOpen: boolean;
      open: () => void;
      close: () => void;
      id: string;
      stitchHost: HTMLDivElement;
    }>
  >;
}

class IframeMessageBus {
  private iframe: () => HTMLIFrameElement | null;
  private origin: string;

  constructor(iframe: () => HTMLIFrameElement | null, origin: string) {
    this.iframe = iframe;
    this.origin = origin;
  }

  post(message: unknown) {
    const iframe = this.iframe();
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(message, this.origin);
    } else {
      console.error(
        "Iframe content window is not available, unable to post message",
        { message },
      );
    }
  }

  init(email: string, patient: PatientFromIframe, ehr: EhrConnector) {
    this.post(parentInitMessage(email, patient, ehr));
  }

  sendAuthToken(authToken: string) {
    this.post(parentSendAuthTokenMessage({ token: authToken }));
  }
}

const StitchIframeContent = ({
  environment,
  dialogWidth = "32.25rem",
  email,
  patient,
  authToken,
  onRefreshAuthToken,
  render: Render,
  ehr,
}: StitchIframeProps) => {
  const [stitchHost, setStitchHost] = React.useState<HTMLDivElement | null>(
    null,
  );

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [iframeReady, setIframeReady] = useState(false);

  const src = useMemo(() => {
    if (environment === "local") {
      return "http://localhost:8081/";
    }

    if (environment === "production") {
      return "https://stitch.elationemr.com/";
    }

    return `https://stitch-web-${environment}.el8-${environment}-services.internal/`;
  }, [environment]);

  useEffect(
    function subscribeToIframeMessages() {
      const bus = new IframeMessageBus(
        () => iframeRef.current,
        new URL(src).origin,
      );

      const handleMessage = (event: MessageEvent) => {
        if (isIframeReadyMessage(event)) {
          setIframeReady(true);

          bus.init(email, patient, ehr);
        }

        // Don't echo a (possibly stale) `authToken` prop when an active
        // refresh callback is available — we'd race the fresh token with
        // the stale one and the iframe would resolve whichever arrives
        // first.
        if (isIframeRequestAuthTokenMessage(event)) {
          if (onRefreshAuthToken) {
            void onRefreshAuthToken()
              .then((token) => {
                if (token) bus.sendAuthToken(token);
              })
              .catch((err) => {
                console.error(
                  "[stitch-iframe] onRefreshAuthToken threw; iframe will time out and surface an error",
                  err,
                );
              });
          } else if (authToken) {
            bus.sendAuthToken(authToken);
          }
        }
      };

      window.addEventListener("message", handleMessage);

      return () => window.removeEventListener("message", handleMessage);
    },
    [src, email, patient, authToken, onRefreshAuthToken, ehr],
  );

  // Render the iframe whenever the parent has a way to supply a token —
  // either a static `authToken` prop or a refresh callback that can mint one
  // on demand. Without either, we have no auth path so nothing to show.
  if (!authToken && !onRefreshAuthToken) {
    return <></>;
  }

  const iframeElement = (
    <iframe
      src={src}
      title="Stitch"
      className="block min-h-0 w-full border-0"
      style={{
        height: "100%",
        width: "100%",
        border: "none",
        display: iframeReady ? undefined : "none",
      }}
      ref={iframeRef}
      allow="microphone"
    />
  );

  const stitchUi = (
    <div id="stitch">
      <FloatingStitchButton label="Value Based Care Assistant">
        {(props) => {
          if (Render) {
            return (
              <>
                <div id="stitch-dialog-host" ref={setStitchHost} />
                {stitchHost && (
                  <Render
                    iframeReady={iframeReady}
                    iframe={iframeRef.current}
                    stitchHost={stitchHost}
                    {...props}
                  >
                    {iframeElement}
                  </Render>
                )}
              </>
            );
          }

          return (
            <>
              <div id="stitch-dialog-host" ref={setStitchHost} />
              {stitchHost && (
                <Dialog
                  modal={false}
                  open={props.isOpen}
                  onOpenChange={(next) => (next ? props.open() : props.close())}
                >
                  <DialogContent
                    className="flex w-full max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-none sm:rounded-xl"
                    style={{ width: dialogWidth, height: "68vh" }}
                    portalContainer={stitchHost}
                  >
                    <DialogHeader className="shrink-0 space-y-1 border-b border-border bg-background px-6 py-4 pr-14 text-left">
                      <DialogTitle className="text-base font-semibold leading-none">
                        Value Based Care Assistant
                      </DialogTitle>
                    </DialogHeader>
                    <div className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-muted/20">
                      {!iframeReady && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-[1px]">
                          <div className="flex flex-col items-center gap-3">
                            <Text2>Initializing…</Text2>
                            <Spinner2 />
                          </div>
                        </div>
                      )}
                      <div className="flex h-full min-h-0 flex-1 flex-col">
                        {iframeElement}
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </>
          );
        }}
      </FloatingStitchButton>
    </div>
  );

  return typeof document !== "undefined"
    ? createPortal(stitchUi, document.body)
    : null;
};

export const StitchIframe = (props: StitchIframeProps) => {
  return (
    <ReactQueryClientProvider queryClient={queryClient}>
      <StitchIframeContent {...props} />
    </ReactQueryClientProvider>
  );
};
